/**
 * Inline trend line. Hand-rolled SVG rather than recharts: these appear inside
 * stat cards by the dozen and must cost nothing to render.
 */

import { useId } from "react";

interface SparklineProps {
  values: number[];
  color?: string;
  height?: number;
  /** Fill the area under the line with a soft gradient. */
  area?: boolean;
  className?: string;
  ariaLabel?: string;
}

const VIEW_W = 100;

export default function Sparkline({
  values,
  color = "currentColor",
  height = 28,
  area = true,
  className = "",
  ariaLabel,
}: SparklineProps) {
  // Per-instance id: two sparklines with identical values but different tones
  // would otherwise share a <linearGradient> id, and url(#id) resolves to the
  // FIRST match in the document — the second card would get the wrong colour.
  const gradientId = `spark-${useId().replace(/:/g, "")}`;
  const clean = values.filter((v) => Number.isFinite(v));
  if (clean.length < 2) {
    return (
      <div
        className={`flex items-center ${className}`}
        style={{ height }}
        aria-hidden="true"
      >
        <div className="h-px w-full bg-line" />
      </div>
    );
  }

  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const span = max - min || 1;
  const stepX = VIEW_W / (clean.length - 1);
  // Leave 1px of headroom top and bottom so strokes are never clipped.
  const y = (v: number) => height - 1 - ((v - min) / span) * (height - 2);

  const points = clean.map((v, i) => `${(i * stepX).toFixed(2)},${y(v).toFixed(2)}`);
  const line = `M ${points.join(" L ")}`;
  const filled = `${line} L ${VIEW_W},${height} L 0,${height} Z`;

  return (
    <svg
      className={className}
      viewBox={`0 0 ${VIEW_W} ${height}`}
      preserveAspectRatio="none"
      width="100%"
      height={height}
      role={ariaLabel ? "img" : "presentation"}
      aria-label={ariaLabel}
    >
      {area && (
        <>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={filled} fill={`url(#${gradientId})`} stroke="none" />
        </>
      )}
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
